// Copyright (c) 2020 WiseTime. All rights reserved.

import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { useEffectOnce } from "react-use"
import { t } from "@lingui/macro"
import styled, { css, SimpleInterpolation } from "styled-components/macro"
import { TagActivity, TextEllipsis } from "features/shared/styles"
import RemoveButton from "features/shared/RemoveButton"
import * as TimelineActions from "../actions"
import * as ActivityTypesActions from "../../activity-types/actions"
import * as ActivityTypesSlice from "../../activity-types/slice"
import { DestinationOneOf } from "features/shared/search-dropdown/types"
import { ActivityTypeRef } from "generated/wisetime/user/type/activity_type_ref_pb"
import { IdList, UnsetActivityTypeRequest } from "generated/wisetime/user/timeline/v4/timeline_pb"
import { ActivityTypesViewType } from "../slices/additionalUi"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { GetActivityTypeRequest } from "../../../generated/legebill/site/activitytype/v4/activity_type_pb"

const Wrapper = styled.div<{ isDisabled: boolean, isDeEmphasized: boolean }>`
  ${TagActivity};
  ${({ isDisabled, isDeEmphasized, theme }): SimpleInterpolation => {
    if (isDeEmphasized) {
      return css`
        background-color: ${theme.colors.gainsboro};
        border-color: ${theme.colors.gainsboro};
        color: ${theme.colors.dimGray};
      `
    }
    if (isDisabled) {
      return css`
        background-color: ${theme.colors.white};
        border-color: ${theme.colors.gainsboro};
        color: ${theme.colors.dimGray};
      `
    }
    return css`
      &:hover {
        & > ${RemoveButton} {
          display: inline-block;
        }
      }
    `
  }}
`

const Label = styled.span`
  ${TextEllipsis};
  display: block;
`

type Props = {
  activityTypeRef: ActivityTypeRef.AsObject,
  timeGroupIdOrTimeRowsIds?: DestinationOneOf,
  isDisabled: boolean,
  isDisabledGroup: boolean,
  stopPropagation?: boolean,
  isDeEmphasized?: boolean,
  className?: string,
  onRemove?: () => void,
}

const ActivityTypeLabel = ({
  activityTypeRef,
  timeGroupIdOrTimeRowsIds = undefined,
  isDisabled,
  isDisabledGroup,
  stopPropagation = true,
  isDeEmphasized = false,
  className,
  onRemove,
}: Props): React.ReactElement<Props> => {
  const dispatch = useDispatch()
  const activityTypesViewType = useSelector(TimelineAdditionalUiSlice.selectActivityTypesViewType)
  const code = useSelector(ActivityTypesSlice.selectActivityTypesCache)[activityTypeRef.id]?.code ?? ""
  const codeWithFallback = code || t({ id: "label_n_a", message: "N/A" })

  useEffectOnce(() => {
    if (code === "") {
      dispatch(ActivityTypesActions.getActivityType(new GetActivityTypeRequest().setId(activityTypeRef.id)))
    }
  })

  const onRemoveActivityType = (): void => {
    if (onRemove) {
      onRemove()
      return
    }
    if (timeGroupIdOrTimeRowsIds === undefined) {
      return
    }
    const request = new UnsetActivityTypeRequest().setActivityTypeId(activityTypeRef.id)
    if (Array.isArray(timeGroupIdOrTimeRowsIds)) {
      request.setTimeRowIds(new IdList().setIdsList(timeGroupIdOrTimeRowsIds))
    } else {
      request.setTimeGroupId(timeGroupIdOrTimeRowsIds)
    }
    dispatch(TimelineActions.unsetActivityType(request))
  }

  const renderLabel = () => {
    if (activityTypesViewType === ActivityTypesViewType.CODE) {
      return codeWithFallback
    }
    return activityTypeRef.label
  }

  return (
    <Wrapper
      tabIndex={-1}
      title={`${codeWithFallback}\n${activityTypeRef.label}`}
      className={className}
      isDisabled={isDisabled}
      isDeEmphasized={isDeEmphasized}
      onClick={(event: React.MouseEvent<HTMLElement>): void => {
        stopPropagation && event.stopPropagation()
      }}
    >
      <Label>{renderLabel()}</Label>
      {(!isDisabled && !isDisabledGroup) && <RemoveButton onClick={onRemoveActivityType} />}
    </Wrapper>
  )
}

export default ActivityTypeLabel
